import { ApiService, EnterpriseAccountRequest, HttpStatus, isHttpError } from "../api";
import { makeAutoObservable } from "mobx";
import { AxiosInstance } from "axios";
import { logDebug } from "../utils/log";
import UserStore from "../stores/UserStore";
import { AcceptCompanyTermsDialogModel } from "../termOfService/TermsOfServiceDialog";
import { History } from "history";
import {
  BR_STATUS,
  CompanyItem,
  emailValidator,
  EnterpriseAccount,
  EnterpriseAccountStatus,
  failure,
  ok,
  pending,
  Portfolio,
  Result,
} from "../common/common";
import { navigateToPortfolio, navigateToUserPortfolio } from "../utils/navigation";
import {
  ActivateSubscriptionController,
  ActiveShareholderRegisterSubscriptionContext,
  SubscriptionTypeSelectionController,
} from "../payment/Payment";
import { PaymentService } from "../payment/PaymentApi";
import {
  SendInvitationDialogParams,
  SendInvitationDialogStore,
} from "../stores/SendInvitationDialogStore";
import {
  NewShareClassController,
  ShareClassApi,
} from "../shareholderRegister/transactions/shareClassDomain";
import { AffiliateService } from "../affiliate/Affiliate";
import { getFullName, validateName } from "../utils/Roles";
import { CompanySearchController } from "../companysearch/SearchController";
import { MeetingType, meetingTypeCaption } from "../meetings/meetingsDomain";
import { Language } from "../i18n/languages";
import AlertMessageStore from "../stores/AlertMessageStore";
import { TMessage } from "../i18n/tt";
import { EditSingleShareholderController } from "../shareholderRegister/shareholders/editSingleShareholderDomain";
import { PortfolioCompany } from "../stores/PortfolioStore";
import { ChangePermissionsModel, ObjectPermission } from "../types/Permissions";
import {
  EditEmailForm,
  InviteNewAdminDialogStore,
  RevokeAllForm,
} from "../portfolio/contacts/contactsDomain";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../common/commonForms";
import { AIClient } from "../ai/AIAssistant";
import { ShareType, ShareTypeCode } from "../common/share";
import { AlertState, ConfirmState } from "../common/alertConfirm";
import { DCEvent, EventDispatcher } from "../events/eventsDomain";
import { Actor, ActorsSelectorModel, ActorType, SelectedActor } from "../types/SelectActors";
import ShareholdersStore from "../stores/ShareholdersStore";
import { PendingDecisionListItem } from "../AdminApp/AdminAppController";
import { ExtendedShareholderDTO } from "../types/Shareholder";
import {
  CustomRoleDetailsModel,
  InviteUserToCustomRoleModel,
} from "../portfolio/contacts/customRoleDomain";
import { combineDateAndTime, today, toLocalDateISOString } from "../utils/Dates";
import {
  ExportShareholderData,
  ExportShareholderModel,
} from "../shareholderRegister/shareholders/ExportShareholdersDomain";
import { AccessType } from "../types/enums/AccessType";
import { Transaction } from "../types/Transactions";

export class VerifyEmailController {
  verificationCodeModel: StringFieldModel;
  updatedEmailResult?: Result<TMessage, TMessage>;

  constructor(
    readonly apiService: ApiService,
    readonly emailAddress: string,
    readonly closeDialog: () => void,
  ) {
    this.verificationCodeModel = new StringFieldModel({ required: true });
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  *verifyUserEmail() {
    this.verificationCodeModel.validate();
    if (!this.verificationCodeModel.isValid) return;

    try {
      this.updatedEmailResult = pending();
      yield this.apiService.verifyUserEmail(
        this.emailAddress,
        this.verificationCodeModel.validatedValue,
      );
      this.updatedEmailResult = ok({ key: "ok" });
    } catch (error) {
      if (isHttpError(HttpStatus.BAD_REQUEST, error)) {
        this.updatedEmailResult = failure({ key: "login.invalid-one-time-code" });
      } else {
        this.updatedEmailResult = failure({ key: "registerUser.updateEmailFailed" });
      }
    }
  }

  *sendVerificationEmail() {
    this.updatedEmailResult = undefined;
    yield this.apiService.sendEmailVerificationCode(this.emailAddress);
    this.verificationCodeModel.clear();
  }

  skipVerification() {
    this.closeDialog();
  }
}

export type AddNewShareClassParams = {
  shareholderStore: ShareholdersStore;
  locale: Language;
  http: AxiosInstance;
  onFinish: (newShareType: ShareType) => void;
};

export class EnterpriseAccountModel {
  name: StringFieldModel;
  status: RadioFieldModel<EnterpriseAccountStatus>;
  startTime: LocalDateFieldModel;
  endTime: LocalDateFieldModel;
  shareholderRegisterLimit: NumberFieldModel;
  digitalCompanyLimit: NumberFieldModel;
  contactName: StringFieldModel;
  contactEmail: StringFieldModel;
  primaryCompanyOrgNo: StringFieldModel;
  keyAccountManager: StringFieldModel;
  hubspotLink: StringFieldModel;
  tripletexLink: StringFieldModel;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly onSuccess: () => void,
    readonly account?: EnterpriseAccount,
  ) {
    this.name = new StringFieldModel({ required: true, value: account?.name });
    this.status = new RadioFieldModel<EnterpriseAccountStatus>({
      value: account?.status ?? EnterpriseAccountStatus.ACTIVE,
      options: Object.values(EnterpriseAccountStatus),
      idFn: (value) => value,
      labelFn: (value) => ({
        key:
          value === EnterpriseAccountStatus.ACTIVE
            ? "enterprise-account.status.active"
            : "enterprise-account.status.inactive",
      }),
    });
    this.startTime = new LocalDateFieldModel({
      required: true,
      value: toLocalDateISOString(account?.startTime ? account.startTime : today()),
    });
    this.endTime = new LocalDateFieldModel({ value: account?.endTime ?? undefined });
    this.shareholderRegisterLimit = new NumberFieldModel({
      value: account?.subscriptionLimits?.shareholder_register,
      isPositiveOrZero: true,
    });
    this.digitalCompanyLimit = new NumberFieldModel({
      value: account?.subscriptionLimits?.digital_company,
      isPositiveOrZero: true,
    });
    this.contactName = new StringFieldModel({
      validator: validateName,
      value: getFullName(account?.personInfo),
    });
    this.contactEmail = new StringFieldModel({
      validator: emailValidator,
      value: account?.personInfo?.email,
    });
    this.primaryCompanyOrgNo = new StringFieldModel({ value: account?.primaryCompanyOrgNo });
    this.keyAccountManager = new StringFieldModel({
      value: account?.metadata.keyAccountManager ?? undefined,
    });
    this.hubspotLink = new StringFieldModel({ value: account?.metadata.hubspotLink ?? undefined });
    this.tripletexLink = new StringFieldModel({
      value: account?.metadata.tripletexLink ?? undefined,
    });

    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  *save() {
    if (!this.validate()) {
      return;
    }
    try {
      if (this.account) {
        yield this.apiService.updateEnterpriseAccount(this.account.id, this.prepareData());
      } else {
        yield this.apiService.createEnterpriseAccount(this.prepareData());
      }
      this.onSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  validate() {
    this.name.validate();
    this.startTime.validate();
    this.contactName.validate();
    this.contactEmail.validate();
    return (
      this.name.isValid &&
      this.startTime.isValid &&
      this.contactName.isValid &&
      this.contactEmail.isValid
    );
  }

  private prepareData(): EnterpriseAccountRequest {
    return {
      name: this.name.validatedValue,
      status: this.status.value,
      subscriptionLimits: {
        shareholder_register: this.shareholderRegisterLimit.parsedValue ?? 0,
        digital_company: this.digitalCompanyLimit.parsedValue ?? 0,
      },
      startTime: combineDateAndTime(this.startTime.validatedValue!, "12:00"),
      endTime: this.endTime.validatedValue
        ? combineDateAndTime(this.endTime.validatedValue, "12:00")
        : undefined,
      contactName: this.contactName.inputValue,
      contactEmail: this.contactEmail.inputValue,
      primaryCompanyOrgNo: this.primaryCompanyOrgNo.validatedValue,
      metadata: {
        keyAccountManager: this.keyAccountManager.validatedValue,
        hubspotLink: this.hubspotLink.validatedValue,
        tripletexLink: this.tripletexLink.validatedValue,
      },
    };
  }
}

export type DialogType =
  | { type: "acceptCompanyTerms"; model: AcceptCompanyTermsDialogModel }
  | { type: "payment"; model: SubscriptionTypeSelectionController }
  | { type: "sendInvitation"; model: SendInvitationDialogStore }
  | { type: "inviteNewAdmin"; model: InviteNewAdminDialogStore }
  | { type: "editEmailAdmin"; model: EditEmailForm }
  | { type: "revokeAllAdminAccess"; model: RevokeAllForm }
  | { type: "addShareType"; model: NewShareClassController }
  | { type: "activateSubscription"; model: ActivateSubscriptionController }
  | { type: "showGeneralMeetingAnnouncement"; shareholderCount: number }
  | {
      type: "activeShareholderRegisterSubscription";
      context: ActiveShareholderRegisterSubscriptionContext;
    }
  | { type: "swedishCompanySearch"; model: CompanySearchController }
  | { type: "norwegianCompanySearch"; model: CompanySearchController }
  | {
      type: "newMeeting";
      model: {
        meetingType: MeetingType;
        organisationNumber: string;
        projectName: TMessage;
        onCancel: () => void;
        projectId?: string;
      };
    }
  | {
      type: "showCreatePortfolioDialog";
      apiService: ApiService;
      onSuccess: (portfolio: Portfolio) => void;
    }
  | {
      type: "preview-file";
      filename?: string;
      file: { url: string } | { data: ArrayBuffer | Blob };
    }
  | {
      type: "editProjectName";
      name: StringFieldModel;
      save: ((name: string) => void) | ((name: string) => DCEvent[]) | DCEvent[];
      isDuplicate?: boolean;
    }
  | {
      type: "verify-email";
      controller: VerifyEmailController;
    }
  | {
      type: "edit-shareholder";
      controller: EditSingleShareholderController;
      infoLabel?: TMessage;
    }
  | {
      type: "change-permissions";
      model: ChangePermissionsModel;
    }
  | {
      type: "confirm";
      state: ConfirmState;
    }
  | {
      type: "alert";
      state: AlertState;
    }
  | {
      type: "select-actors";
      model: ActorsSelectorModel;
    }
  | {
      type: "decision-approval-date";
      decision: PendingDecisionListItem;
      status: BR_STATUS;
      model: LocalDateFieldModel;
    }
  | {
      type: "submit-for-signing-success";
      orgNo?: string;
    }
  | {
      type: "enterprise-account";
      model: EnterpriseAccountModel;
    }
  | {
      type: "open-role-details";
      model: CustomRoleDetailsModel;
    }
  | {
      type: "invite-custom-role-user";
      model: InviteUserToCustomRoleModel;
    }
  | {
      type: "export-shareholders";
      model: ExportShareholderModel;
    }
  | {
      type: "change-transaction-date";
      model: ChangeTransactionDateModel;
      onClose: () => void;
    };

export class DialogStore {
  readonly dispatch: EventDispatcher;
  readonly userStore: UserStore;
  readonly apiService: ApiService;
  readonly history: History;
  readonly http: AxiosInstance;
  readonly paymentService: PaymentService;
  messageStore: AlertMessageStore;

  constructor(
    dispatch: EventDispatcher,
    userStore: UserStore,
    apiService: ApiService,
    history: History,
    http: AxiosInstance,
    messageStore: AlertMessageStore,
  ) {
    this.dispatch = dispatch;
    this.userStore = userStore;
    this.apiService = apiService;
    this.history = history;
    this.http = http;
    this.messageStore = messageStore;
    this.paymentService = new PaymentService(http);

    makeAutoObservable(
      this,
      { userStore: false, apiService: false, history: false, http: false },
      { autoBind: true },
    );
  }

  openDialog(dialog: DialogType) {
    logDebug("Dialogs.openDialog", dialog);
    this.dispatch([{ type: "show-dialog", dialog }]);
  }

  closeDialog() {
    this.dispatch([{ type: "closeDialog" }]);
  }

  openAcceptCompanyTermsDialog() {
    const onDecline = () => {
      this.closeDialog();
      navigateToPortfolio(this.history);
    };

    this.openDialog({
      type: "acceptCompanyTerms",
      model: new AcceptCompanyTermsDialogModel(
        this.http,
        this.userStore,
        this.closeDialog,
        onDecline,
      ),
    });
  }

  openSubscriptionDialog(lang: Language, userStore: UserStore, affiliateService: AffiliateService) {
    if (userStore.hasInactiveShareholderRegisterSubscription) {
      this.openActivateSubscriptionDialog(userStore);
    } else if (userStore.hasActiveShareholderRegisterSubscription) {
      this.openActiveShareholderRegisterSubscriptionDialog(userStore);
    } else {
      this.openPaymentDialog(lang, userStore, affiliateService);
    }
  }

  openCreatePortfolioDialog() {
    this.openDialog({
      type: "showCreatePortfolioDialog",
      apiService: this.apiService,
      onSuccess: (p) => {
        navigateToUserPortfolio(p.id, this.history);
        this.userStore.portfolios.push(p);
        this.messageStore.showSuccess({ key: "portfolio.created" }, { autoHide: false });
      },
    });
  }

  openPaymentDialog(locale: Language, userStore: UserStore, affiliateService: AffiliateService) {
    const user = userStore.user;
    const companyInfo = userStore.companyInfo;
    if (user && companyInfo) {
      Promise.all([
        this.paymentService.findAvailableProducts(),
        this.paymentService.findProductSubscription(companyInfo.company.organizationnumber),
        affiliateService.getAffiliateById(companyInfo.company.affiliateId),
      ]).then(([listProducts, existingSubscription, affiliate]) => {
        const model = new SubscriptionTypeSelectionController({
          paymentService: this.paymentService,
          onCancel: () => this.closeDialog(),
          onFinish: () => this.userStore.refreshUserInformation(),
          userContext: {
            companyName: companyInfo.company.name,
            administratorName: getFullName(user),
            administratorEmail: this.userStore.user?.email ?? "",
            organisationNumber: companyInfo.company.organizationnumber,
            currentShareholderCount: Object.values(companyInfo.numberOfSharesPerShareholder).length,
            existingSubscription,
            affiliate,
          },
          locale,
          listProducts,
        });

        this.openDialog({
          type: "payment",
          model,
        });
      });
    }
  }

  openSendInvitationDialog(params: SendInvitationDialogParams) {
    this.openDialog({
      type: "sendInvitation",
      model: new SendInvitationDialogStore(
        params,
        this.apiService,
        this.messageStore,
        this.closeDialog,
        new AIClient(this.http, this.userStore.orgNumber),
        params.customRoleName !== undefined
          ? {
              key: "invite-user.email.default-text-custom-role",
              params: {
                senderName: getFullName(this.userStore.user),
                email: this.userStore.user?.email,
                roleName: params.customRoleName,
                companyName: this.userStore.companyName,
              },
            }
          : {
              key: "invite-user.email.default-text-administrator",
              params: {
                senderName: getFullName(this.userStore.user),
                email: this.userStore.user?.email,
                companyName: this.userStore.companyName,
              },
            },
      ),
    });
  }

  openInviteNewAdminDialog({
    lang,
    companies,
    companyItems,
  }: {
    lang: Language;
    companies: PortfolioCompany[];
    companyItems: CompanyItem[];
  }) {
    this.openDialog({
      type: "inviteNewAdmin",
      model: new InviteNewAdminDialogStore({
        locale: lang,
        onClose: this.closeDialog,
        companies,
        apiService: this.apiService,
        messageStore: this.messageStore,
        companyItems,
      }),
    });
  }

  openAddNewShareClassDialog({ shareholderStore, locale, http, onFinish }: AddNewShareClassParams) {
    const usedCodes = new Set(shareholderStore.activeShareTypes.map((s) => s.sharetypeCode));
    const controller = new NewShareClassController(
      shareholderStore.userStore.orgNumber,
      new ShareClassApi(http),
      this.closeDialog,
      shareholderStore.shareTypeCodes
        .map((d) => d as ShareTypeCode)
        .filter((entry) => !usedCodes.has(entry)),
      locale,
      onFinish,
    );
    this.openDialog({ type: "addShareType", model: controller });
  }

  openNewMeetingDialog(
    organisationNumber: string,
    meetingType: MeetingType,
    projectName?: TMessage,
    projectId?: string,
  ) {
    this.openDialog({
      type: "newMeeting",
      model: {
        meetingType: meetingType,
        organisationNumber: organisationNumber,
        onCancel: () => {
          this.closeDialog();
        },
        projectName: projectName ?? meetingTypeCaption({ meetingType }),
        projectId: projectId,
      },
    });
  }

  openActivateSubscriptionDialog(userStore: UserStore) {
    const companyInfo = userStore.companyInfo;
    if (companyInfo) {
      this.openDialog({
        type: "activateSubscription",
        model: new ActivateSubscriptionController({
          onCloseDialog: this.closeDialog,
          companyName: companyInfo.company.name,
          organisationNumber: companyInfo.company.organizationnumber,
          http: this.http,
          onFinishDialog: () => this.userStore.refreshUserInformation(),
        }),
      });
    }
  }

  openActiveShareholderRegisterSubscriptionDialog(userStore: UserStore) {
    const companyInfo = userStore.companyInfo;
    if (companyInfo) {
      this.openDialog({
        type: "activeShareholderRegisterSubscription",
        context: { onCloseDialog: this.closeDialog, companyName: companyInfo.company.name },
      });
    }
  }

  openSwedishCompanySearch() {
    this.openDialog({
      type: "swedishCompanySearch",
      model: this.createCompanySearchController(),
    });
  }

  openNorwegianCompanySearch() {
    this.openDialog({
      type: "norwegianCompanySearch",
      model: this.createCompanySearchController(),
    });
  }

  private createCompanySearchController() {
    return new CompanySearchController(
      (reload: boolean) => {
        this.closeDialog();
        if (reload) {
          this.userStore.refreshUserInformation();
        }
      },
      (query) => this.apiService.searchSwedishBusinessRegister(query),
      (query) => this.apiService.getCompanies(query),
      (organisationNumber, companyName, email) =>
        this.apiService.askForPermission({ organisationNumber, companyName, email }),
      (organisationNumber) => this.apiService.findOnboardingCandidates(organisationNumber),
    );
  }

  openVerifyEmailDialog(emailAddress: string) {
    this.openDialog({
      type: "verify-email",
      controller: new VerifyEmailController(this.apiService, emailAddress, () => {
        this.closeDialog();
        this.userStore.refreshUserInformation();
      }),
    });
  }

  openEditShareholderDialog({
    orgNumber,
    locale,
    shareholderId,
    onSuccess,
    isShareholderView,
    infoLabel,
    loadShareholders,
  }: {
    orgNumber: string;
    locale: Language;
    onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
    shareholderId?: number;
    isShareholderView?: boolean;
    infoLabel?: TMessage;
    loadShareholders: () => Promise<Actor[]>;
  }) {
    const isCustomRole = this.userStore.accessType === AccessType.CUSTOM;
    const adminCompanies = this.userStore.companies.filter(
      (c) => c.accessType === AccessType.ADMIN,
    );
    const portfolios = this.userStore.portfolios;
    this.openDialog({
      type: "edit-shareholder",
      controller: new EditSingleShareholderController({
        apiService: this.apiService,
        messageStore: this.messageStore,
        orgNumber,
        onSuccess,
        onClose: this.closeDialog,
        locale,
        shareholderId,
        isShareholderView,
        loadShareholders,
        portfolios,
        adminCompanies,
        isCustomRole,
      }),
      infoLabel,
    });
  }

  openChangePermissionDialog(
    title: string | TMessage,
    objectId: string,
    onUpdate: (permissions: ObjectPermission[]) => void,
  ) {
    this.openDialog({
      type: "change-permissions",
      model: new ChangePermissionsModel(
        title,
        this.apiService,
        objectId,
        onUpdate,
        this.closeDialog,
        this.messageStore,
        this.userStore,
        new AIClient(this.http, this.userStore.orgNumber),
      ),
    });
  }

  openGeneralMeetingReleaseDialog() {
    this.openDialog({
      type: "showGeneralMeetingAnnouncement",
      shareholderCount: Object.values(
        this.userStore.companyInfo?.numberOfSharesPerShareholder ?? [],
      ).length,
    });
  }

  openActorSelectorDialog({
    title,
    organisationNumber,
    tabs,
    excludeActors,
    onLoad,
    onSubmit,
    isMultiSelect = true,
    excludeCompanies = false,
  }: {
    title: TMessage;
    organisationNumber: string;
    tabs: ActorType[];
    excludeActors: string[];
    onLoad: (
      type: ActorType,
      onSuccess: (result: Actor[]) => DCEvent[],
      onFailure: () => DCEvent[],
    ) => void;
    onSubmit: (result: SelectedActor[], onsuccess: () => void, onError: () => void) => void;
    isMultiSelect?: boolean;
    excludeCompanies?: boolean;
  }) {
    this.openDialog({
      type: "select-actors",
      model: new ActorsSelectorModel({
        title,
        organisationNumber,
        tabs,
        excludeActors,
        onLoad,
        onSubmit: (data, onError) => {
          onSubmit(data, this.closeDialog, () => {
            onError();
            this.messageStore.showGeneralError();
          });
        },
        onClose: this.closeDialog,
        isMultiSelect,
        excludeCompanies,
      }),
    });
  }

  openEnterpriseAccountDialog(onSuccess: () => void, data?: EnterpriseAccount) {
    this.openDialog({
      type: "enterprise-account",

      model: new EnterpriseAccountModel(
        this.apiService,
        this.messageStore,
        () => {
          onSuccess();
          this.closeDialog();
        },
        data,
      ),
    });
  }

  openExportShareholdersDialog({
    onSubmit,
    selectedContext,
  }: {
    onSubmit: (data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) => void;
    selectedContext?: string;
  }) {
    const isCustomRole = this.userStore.accessType === AccessType.CUSTOM;
    const adminCompanies = this.userStore.companies.filter(
      (c) => c.accessType === AccessType.ADMIN,
    );
    const portfolios = this.userStore.portfolios;
    this.openDialog({
      type: "export-shareholders",
      model: new ExportShareholderModel(
        onSubmit,
        this.closeDialog,
        adminCompanies,
        portfolios,
        isCustomRole,
        selectedContext,
      ),
    });
  }

  openChangeTransactionDateDialog({
    selectedTransaction,
    controller,
    infoText,
  }: {
    selectedTransaction: Transaction;
    controller: {
      transactionLog: Transaction[];
      doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
      cancelChangeTransactionDate: () => void;
      changeDateResult?: Result<TMessage, TMessage>;
    };
    infoText?: TMessage;
  }) {
    return this.openDialog({
      type: "change-transaction-date",
      model: new ChangeTransactionDateModel(selectedTransaction, controller, infoText),
      onClose: this.closeDialog,
    });
  }
}

class ChangeTransactionDateModel {
  selectedTransaction: Transaction;
  controller: {
    transactionLog: Transaction[];
    doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
    cancelChangeTransactionDate: () => void;
    changeDateResult?: Result<TMessage, TMessage>;
  };
  infoText?: TMessage;

  constructor(
    selectedTransaction: Transaction,
    controller: {
      transactionLog: Transaction[];
      doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
      cancelChangeTransactionDate: () => void;
      changeDateResult?: Result<TMessage, TMessage>;
    },
    infoText?: TMessage,
  ) {
    this.selectedTransaction = selectedTransaction;
    this.controller = controller;
    this.infoText = infoText;
  }
}
